import { SERVICE_KEY, type ServiceKey } from './definitions';
import { PERMISSIONS } from './permissions';

/**
 * Route Permission Configuration
 *
 * This module defines the mapping between frontend routes and their required backend permissions.
 * It provides utility functions to match routes to their permission requirements for both
 * admin and expert applications.
 */

export interface RoutePermissionConfig {
  /**
   * The service key that owns these permissions (e.g., SERVICE_KEY.ADMIN)
   */
  serviceKey: ServiceKey;

  /**
   * Array of required permissions in the format 'resource:action'
   * Examples: ['users:read'], ['roles:read', 'roles:create']
   */
  requiredPermissions: string[];

  /**
   * If true, user must have ALL permissions listed.
   * If false, user only needs ANY of the permissions listed.
   * @default true
   */
  requireAll?: boolean;
}

/**
 * Admin Route Permission Mappings
 *
 * Maps admin console routes to their required permissions.
 * Based on permissions defined in libs/auth-api-lib/core/admin_service_permissions.go
 */
export const ADMIN_ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  '/dashboard': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.DASHBOARD_READ],
    requireAll: true,
  },
  '/roles': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.ROLES_READ],
    requireAll: true,
  },
  '/user': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.USERS_READ],
    requireAll: true,
  },
  '/user/add': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.USERS_CREATE],
    requireAll: true,
  },
  '/user/edit': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.USERS_UPDATE],
    requireAll: true,
  },
  '/sessions': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.USERS_READ],
    requireAll: true,
  },
  '/registrations': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.REGISTRATIONS_READ],
    requireAll: true,
  },
  '/company': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.COMPANIES_READ],
    requireAll: true,
  },
  '/company/add': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.COMPANIES_CREATE],
    requireAll: true,
  },
  '/company/edit': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.COMPANIES_UPDATE],
    requireAll: true,
  },
  '/services': {
    serviceKey: SERVICE_KEY.ADMIN,
    requiredPermissions: [PERMISSIONS.ADMIN.SERVICE_READ],
    requireAll: true,
  },
};

/**
 * Expert Route Permission Mappings
 *
 * Maps expert console routes to their required permissions.
 * Based on permissions defined in libs/auth-api-lib/core/expert_service_permission.go
 */
export const EXPERT_ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  '/dashboard': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.DASHBOARD_READ,
      PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_TOTAL_READ,
      PERMISSIONS.EXPERT.DASHBOARD_EXPERTS_ACTIVE_READ,
      PERMISSIONS.EXPERT.DASHBOARD_ATTENDANCE_PRESENT_READ,
      PERMISSIONS.EXPERT.DASHBOARD_COMPANY_TOTALS_READ,
    ],
    requireAll: false,
  },
  '/expert': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.EXPERTS_READ],
    requireAll: true,
  },
  '/personnel': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.COMPANY_PERSONNEL_READ],
    requireAll: true,
  },
  '/expert/reports': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.REPORTS_EXPERTISE_WORKFORCE_MIX_READ,
      PERMISSIONS.EXPERT.REPORTS_ORGANIZATION_UTILIZATION_READ,
      PERMISSIONS.EXPERT.REPORTS_BRANCH_COVERAGE_READ,
    ],
    requireAll: false,
  },
  '/expert/reports/expertise-workforce-mix': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.REPORTS_EXPERTISE_WORKFORCE_MIX_READ,
    ],
    requireAll: true,
  },
  '/expert/reports/organization-utilization': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.REPORTS_ORGANIZATION_UTILIZATION_READ,
    ],
    requireAll: true,
  },
  '/expert/reports/branch-coverage': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.REPORTS_BRANCH_COVERAGE_READ],
    requireAll: true,
  },
  '/attendance-tracking': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_READ],
    requireAll: true,
  },
  '/attendance': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_READ],
    requireAll: true,
  },
  '/attendance/reports': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.ATTENDANCE_DAILY_SUMMARY_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_SUMMARY_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_HISTORY_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERTISE_ANALYTICS_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_TIME_ANALYTICS_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_LOCATION_BASED_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_ACTIVE_SESSIONS_READ,
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_DETAILED_ATTENDANCE_READ,
    ],
    requireAll: false, // User needs ANY of these report permissions
  },
  '/attendance/reports/daily-summary': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_DAILY_SUMMARY_READ],
    requireAll: true,
  },
  '/attendance/reports/expert-summary': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_SUMMARY_READ],
    requireAll: true,
  },
  '/attendance/reports/expert-history': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_HISTORY_READ],
    requireAll: true,
  },
  '/attendance/reports/expertise-analytics': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERTISE_ANALYTICS_READ,
    ],
    requireAll: true,
  },
  '/attendance/reports/time-analytics': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_TIME_ANALYTICS_READ],
    requireAll: true,
  },
  '/attendance/reports/location-based': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_LOCATION_BASED_READ],
    requireAll: true,
  },
  '/attendance/reports/active-sessions': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_ACTIVE_SESSIONS_READ],
    requireAll: true,
  },
  '/attendance/reports/expert-detailed-attendance': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [
      PERMISSIONS.EXPERT.ATTENDANCE_EXPERT_DETAILED_ATTENDANCE_READ,
    ],
    requireAll: true,
  },
  '/referrals': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.REFERRAL_READ],
    requireAll: true,
  },
  '/commissions': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.COMMISSION_READ],
    requireAll: true,
  },
  '/commission-rules': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.COMMISSION_RULE_READ],
    requireAll: true,
  },
  '/referral-source-channels': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.REFERRAL_SOURCE_CHANNEL_READ],
    requireAll: true,
  },
  '/organizations': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ORGANIZATION_READ],
    requireAll: true,
  },
  '/organizations/add': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ORGANIZATION_CREATE],
    requireAll: true,
  },
  '/industries': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.INDUSTRY_READ],
    requireAll: true,
  },
  '/industries/add': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.INDUSTRY_CREATE],
    requireAll: true,
  },
  '/industries/hierarchy': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.INDUSTRY_READ],
    requireAll: true,
  },
  '/api-token-settings': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.API_TOKEN_READ],
    requireAll: true,
  },
  '/attendance-settings': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_READ],
    requireAll: true,
  },
  '/custom-attributes': {
    serviceKey: SERVICE_KEY.EXPERT,
    requiredPermissions: [PERMISSIONS.EXPERT.CUSTOM_ATTRIBUTE_READ],
    requireAll: true,
  },
};

/**
 * Audit Route Permission Mappings
 */
export const AUDIT_ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  '/audit-logs': {
    serviceKey: SERVICE_KEY.AUDIT,
    requiredPermissions: [PERMISSIONS.AUDIT.AUDIT_LOGS_READ],
    requireAll: true,
  },
};

/**
 * Match a route to its admin permission configuration
 *
 * Performs exact matching first, then falls back to prefix matching for nested routes.
 * For example, '/roles/123' will match '/roles' configuration.
 *
 * @param route - The route to match (e.g., '/dashboard', '/roles/123')
 * @returns RoutePermissionConfig if a match is found, null otherwise
 */
export function matchAdminRoutePermission(
  route: string
): RoutePermissionConfig | null {
  // Normalize route by removing trailing slashes
  const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;

  // Try exact match first
  if (ADMIN_ROUTE_PERMISSIONS[normalizedRoute]) {
    return ADMIN_ROUTE_PERMISSIONS[normalizedRoute];
  }

  // Try prefix matching for nested routes (e.g., /roles/123 matches /roles)
  const matchingRoute = Object.keys(ADMIN_ROUTE_PERMISSIONS).find((key) =>
    normalizedRoute.startsWith(key + '/')
  );

  return matchingRoute ? ADMIN_ROUTE_PERMISSIONS[matchingRoute] : null;
}

/**
 * Match a route to its expert permission configuration
 *
 * Performs exact matching first, then falls back to prefix matching for nested routes.
 * For example, '/expert/123' will match '/expert' configuration.
 *
 * @param route - The route to match (e.g., '/dashboard', '/expert/123')
 * @returns RoutePermissionConfig if a match is found, null otherwise
 */
export function matchExpertRoutePermission(
  route: string
): RoutePermissionConfig | null {
  // Normalize route by removing trailing slashes
  const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;

  // Try exact match first
  if (EXPERT_ROUTE_PERMISSIONS[normalizedRoute]) {
    return EXPERT_ROUTE_PERMISSIONS[normalizedRoute];
  }

  // Try prefix matching for nested routes (e.g., /expert/123 matches /expert)
  const matchingRoute = Object.keys(EXPERT_ROUTE_PERMISSIONS).find((key) =>
    normalizedRoute.startsWith(key + '/')
  );

  return matchingRoute ? EXPERT_ROUTE_PERMISSIONS[matchingRoute] : null;
}

/**
 * Match a route to its audit permission configuration.
 */
export function matchAuditRoutePermission(
  route: string
): RoutePermissionConfig | null {
  const normalizedRoute = route.endsWith('/') ? route.slice(0, -1) : route;

  if (AUDIT_ROUTE_PERMISSIONS[normalizedRoute]) {
    return AUDIT_ROUTE_PERMISSIONS[normalizedRoute];
  }

  const matchingRoute = Object.keys(AUDIT_ROUTE_PERMISSIONS).find((key) =>
    normalizedRoute.startsWith(key + '/')
  );

  return matchingRoute ? AUDIT_ROUTE_PERMISSIONS[matchingRoute] : null;
}
